import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import api from '../api';

const SOCKET_URL = process.env.REACT_APP_API_URL || '';
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('luna_token'));
  const [loading, setLoading] = useState(!!token);
  const socketRef = useRef(null);
  const [socket, setSocket] = useState(null);

  const fetchUser = useCallback(async (t, retries = 3) => {
    try {
      const data = await api.me(t);
      setUser(data.user || data);
    } catch (err) {
      // Only clear token on definitive auth errors (401/403)
      if (err?.status === 401 || err?.status === 403) {
        localStorage.removeItem('luna_token');
        setToken(null);
        setUser(null);
      } else if (retries > 0) {
        // Network/server error (e.g., backend restarting) — retry with backoff
        const delay = (4 - retries) * 2000; // 2s, 4s, 6s
        await new Promise(r => setTimeout(r, delay));
        return fetchUser(t, retries - 1);
      }
      // If all retries failed with non-auth error, keep token but no user
      // Start a background check to recover when server comes back
      if (retries === 0 && t) {
        setTimeout(() => {
          if (!document.hidden) fetchUser(t, 1);
        }, 10000);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchUser(token);
    else setLoading(false);
  }, [token, fetchUser]);

  // Global socket connection
  useEffect(() => {
    if (!token) {
      if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; setSocket(null); }
      return;
    }
    const s = io(SOCKET_URL, { auth: { token }, transports: ['polling', 'websocket'], reconnection: true, reconnectionDelay: 2000 });
    socketRef.current = s;
    setSocket(s);

    // Request browser notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => { s.disconnect(); socketRef.current = null; setSocket(null); };
  }, [token]);

  const login = async (email, password) => {
    const data = await api.login(email, password);
    const t = data.token;
    localStorage.setItem('luna_token', t);
    setToken(t);
    setLoading(true);
    await fetchUser(t);
    return data;
  };

  const register = async (body) => {
    const data = await api.register(body);
    // No longer returns token — user must verify email first
    return data;
  };

  const logout = () => {
    localStorage.removeItem('luna_token');
    setToken(null);
    setUser(null);
    if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; setSocket(null); }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, socket }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
